import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  GraduationCap, 
  LogOut, 
  Users, 
  DollarSign, 
  FileText, 
  TrendingUp,
  Settings,
  Mail,
  Database,
  Edit,
  Save,
  FileDown,
  Search,
  Filter,
  X,
  Calendar,
  MapPin,
  BookOpen
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import UserManagement from '../components/admin/UserManagement';
import CMSPageEditor from '../components/admin/CMSPageEditor';
import FooterEditor from '../components/admin/FooterEditor';
import ReferralManagement from '../components/admin/ReferralManagement';
import WithdrawalRequests from '../components/admin/WithdrawalRequests';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import PaymentGatewaySettings from '../components/admin/PaymentGatewaySettings';
import TestSeriesAdmin from '../components/TestSeriesAdmin';
import FormInstructionsAdmin from '../components/admin/FormInstructionsAdmin';
import PaywallAdmin from '../components/admin/PaywallAdmin';
import LeadsAdmin from '../components/admin/LeadsAdmin';
import VerificationSettings from '../components/admin/VerificationSettings';
import EmailProviderSettings from '../components/admin/EmailProviderSettings';
import ForumAdmin from '../components/admin/ForumAdmin';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // CMS Content State
  const [cmsContent, setCmsContent] = useState({
    hero: {
      title: 'Predict Your College Admission',
      subtitle: 'Get accurate predictions for IITs, NITs, IIITs, and other top engineering colleges based on your JEE rank with AI-powered analysis'
    },
    features: [
      {
        title: 'Comprehensive Database',
        description: 'Access data from 1000+ IITs, NITs, IIITs, GFTIs, and top private colleges'
      },
      {
        title: 'Historical Trends',
        description: 'Analyze 5-year cutoff trends with AI-powered predictions'
      },
      {
        title: 'PDF Report & Email',
        description: 'Get detailed PDF report with predictions sent to your email'
      }
    ],
    pricing: {
      amount: 299,
      currency: 'INR'
    },
    referral: {
      amount: 200,
      enabled: true,
      minWithdrawalAmount: 1000
    }
  });

  // PDF Template State
  const [pdfTemplate, setPdfTemplate] = useState({
    header: 'College Kampus - JEE Predictor',
    subheader: 'User Prediction Report',
    footer: '© 2024 College Kampus. All rights reserved.',
    brandingText: 'College Kampus',
    includeUserInfo: true,
    includeExamDetails: true,
    includeHistoricalData: true,
    includeCutoffTrends: true,
    // Colors (hex format)
    headerColor: '#2563eb', // blue-600
    tableHeaderBgColor: '#2563eb',
    tableHeaderTextColor: '#ffffff',
    // Chance colors
    chanceColors: {
      high: '#22c55e', // green
      medium: '#a16207', // brown
      low: '#ef4444' // red
    },
    // Table columns
    tableColumns: {
      serialNumber: true,
      collegeName: true,
      branch: true,
      location: true,
      cutoffRank: true,
      fees: true,
      chance: true
    },
    // Font settings
    fontSize: {
      header: 18,
      subheader: 12,
      body: 10,
      table: 8,
      chance: 9
    },
    // Custom sections
    customSections: [],
    // Global styling
    globalBackgroundColor: '#ffffff',
    globalBorderStyle: 'solid',
    globalBorderThickness: 1,
    globalBorderColor: '#cccccc',
    // Section-specific styling
    sectionStyles: {
      header: {
        backgroundColor: '#ffffff',
        borderStyle: 'solid',
        borderThickness: 1,
        borderColor: '#cccccc'
      },
      userInfo: {
        backgroundColor: '#f8f9fa',
        borderStyle: 'solid',
        borderThickness: 1,
        borderColor: '#dee2e6'
      },
      table: {
        backgroundColor: '#ffffff',
        borderStyle: 'solid',
        borderThickness: 1,
        borderColor: '#cccccc'
      },
      customSections: {
        backgroundColor: '#f8f9fa',
        borderStyle: 'solid',
        borderThickness: 1,
        borderColor: '#dee2e6'
      },
      footer: {
        backgroundColor: '#ffffff',
        borderStyle: 'none',
        borderThickness: 0,
        borderColor: '#cccccc'
      }
    }
  });
  const [showPDFPreview, setShowPDFPreview] = useState(false);

  // Discount Coupons State
  const [coupons, setCoupons] = useState([
    { id: 1, code: 'SAVE50', discount: 50, type: 'flat', active: true },
    { id: 2, code: 'NEW100', discount: 100, type: 'flat', active: true },
    { id: 3, code: 'STUDENT20', discount: 20, type: 'percentage', active: true }
  ]);
  
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount: '',
    type: 'flat'
  });

  // SMTP Settings State
  const [smtpSettings, setSmtpSettings] = useState({
    host: 'smtp.gmail.com',
    port: 587,
    user: 'your-email@gmail.com',
    password: '********',
    fromName: 'JEE Predictor Pro'
  });

  // Counseling Dates State - Enhanced
  const [counselingDates, setCounselingDates] = useState({
    jee_counseling: {
      active: true,
      rounds: [
        {
          id: 1,
          name: 'JoSAA Round 1',
          registration_start: '',
          registration_end: '',
          choice_filling_start: '',
          choice_filling_end: '',
          seat_allotment_date: '',
          payment_deadline: '',
          reporting_start: '',
          reporting_end: '',
          active: true
        }
      ]
    },
    state_counseling: {
      active: true,
      states: [
        {
          id: 1,
          state_name: '',
          counseling_name: '',
          registration_start: '',
          registration_end: '',
          choice_filling_start: '',
          choice_filling_end: '',
          seat_allotment_date: '',
          website_url: '',
          active: true
        }
      ]
    },
    important_dates: [],
    general_notes: ''
  });

  // Counseling Requests State
  const [counselingRequests, setCounselingRequests] = useState([]);
  const [counselingRequestsLoading, setCounselingRequestsLoading] = useState(false);

  // Admin Password Change State
  const [passwordChange, setPasswordChange] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // User Roles State
  const [userRoles, setUserRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);

  // SMS Gateway Settings State
  const [smsSettings, setSmsSettings] = useState({
    active_gateway: 'demo',
    demo_enabled: true,
    msg91_enabled: false,
    msg91_auth_key: '',
    msg91_sender_id: 'MSGAPI',
    msg91_route: 'default',
    fast2sms_enabled: false,
    fast2sms_api_key: '',
    fast2sms_sender_id: 'FSTSMS',
    fast2sms_route: 'otp',
    otp_length: 6,
    otp_expiry_minutes: 5,
    otp_max_attempts: 3
  });

  // College Edit State
  const [editingCollege, setEditingCollege] = useState(null);
  const [editCollegeModal, setEditCollegeModal] = useState(false);
  const [editBranchModal, setEditBranchModal] = useState(false);
  
  // College List State
  const [dbColleges, setDbColleges] = useState([]);
  const [collegesLoading, setCollegesLoading] = useState(false);
  const [collegePage, setCollegePage] = useState(1);
  const [collegeTotalPages, setCollegeTotalPages] = useState(1);
  const [collegesPerPage, setCollegesPerPage] = useState(50);
  const [collegeTypeFilter, setCollegeTypeFilter] = useState('');
  const [collegeStateFilter, setCollegeStateFilter] = useState('');
  const [collegeCityFilter, setCollegeCityFilter] = useState('');
  const [collegeStats, setCollegeStats] = useState({
    total: 0,
    iit: 0,
    nit: 0,
    iiit: 0,
    gfti: 0,
    jee_ranked: 0
  });
  
  // College Search/Filter State
  const [collegeSearchTerm, setCollegeSearchTerm] = useState('');
  const [collegeFilters, setCollegeFilters] = useState({
    state: '',
    city: '',
    minFees: '',
    maxFees: '',
    minRank: '',
    maxRank: ''
  });
  const [editingBranch, setEditingBranch] = useState(null);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
    }
  }, [navigate]);

  // Load colleges function - moved outside useEffect to be accessible from onClick handlers
  const loadColleges = async (page = 1, perPage = 50, typeFilter = '', stateFilter = '', cityFilter = '', accreditationFilter = '') => {
    setCollegesLoading(true);
    try {
      // Build query params
      let url = `${BACKEND_URL}/api/admin/colleges/list?skip=${(page-1)*perPage}&limit=${perPage}`;
      
      // Add filters
      if (typeFilter) {
        if (typeFilter === 'JEE-Rank-Based-Admissions') {
          url += `&category=${typeFilter}`;
        } else {
          url += `&type=${typeFilter}`;
        }
      }
      if (stateFilter) url += `&state=${encodeURIComponent(stateFilter)}`;
      if (cityFilter) url += `&city=${encodeURIComponent(cityFilter)}`;
      if (accreditationFilter) url += `&accreditation=${encodeURIComponent(accreditationFilter)}`;
      
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setDbColleges(data.colleges);
        setCollegeTotalPages(data.pages);
      }
    } catch (error) {
      console.error('Load colleges error:', error);
    } finally {
      setCollegesLoading(false);
    }
  };

  const loadCollegeStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/colleges/stats`);
      const data = await response.json();
      if (data.success) {
        setCollegeStats(data.stats);
      }
    } catch (error) {
      console.error('Load stats error:', error);
    }
  };

  const handleStatCardClick = (type) => {
    setCollegeTypeFilter(type);
    setCollegePage(1);
    setCollegeStateFilter('');
    setCollegeCityFilter('');
    loadColleges(1, collegesPerPage, type, '', '');
    
    // Scroll to colleges list
    document.getElementById('colleges-list')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/colleges/export-csv`);
      const data = await response.json();
      
      if (data.success) {
        // Create blob and download
        const blob = new Blob([data.csv_content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast({ title: "Success", description: "CSV file downloaded successfully" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to download CSV", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast({ title: "Logged out", description: "You have been logged out successfully" });
    navigate('/admin');
  };

  const handleSaveCMS = () => {
    // In real app, this would call backend API
    toast({ title: "Success", description: "CMS content saved successfully" });
  };

  const handleSavePDFTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pdf-template`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pdfTemplate)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "PDF template saved successfully" });
      } else {
        toast({ 
          title: "Error", 
          description: data.message || "Failed to save PDF template",
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('Save PDF template error:', error);
      toast({ 
        title: "Error", 
        description: "Failed to save PDF template",
        variant: "destructive" 
      });
    }
  };

  // Load PDF template on component mount
  useEffect(() => {
    const loadPDFTemplate = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/pdf-template`);
        const data = await response.json();
        if (data) {
          // Merge with default state to ensure all properties exist
          setPdfTemplate(prevState => ({
            ...prevState,
            ...data,
            chanceColors: data.chanceColors || prevState.chanceColors,
            tableColumns: data.tableColumns || prevState.tableColumns,
            fontSize: data.fontSize || prevState.fontSize,
            sectionStyles: data.sectionStyles || prevState.sectionStyles
          }));
        }
      } catch (error) {
        console.error('Load PDF template error:', error);
      }
    };
    
    const loadCounselingDates = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/counseling-dates`);
        const data = await response.json();
        if (data && response.ok) {
          // Ensure we have default values if API returns empty strings
          setCounselingDates({
            current_round: data.current_round || 'Round 1',
            current_round_start: data.current_round_start || '',
            current_round_end: data.current_round_end || '',
            next_round: data.next_round || 'Round 2',
            next_round_tentative_start: data.next_round_tentative_start || '',
            important_dates: data.important_dates || [],
            notes: data.notes || ''
          });
        }
      } catch (error) {
        console.error('Load counseling dates error:', error);
      }
    };
    
    const loadSMSSettings = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/sms-settings`);
        const data = await response.json();
        if (data && response.ok) {
          setSmsSettings(data);
        }
      } catch (error) {
        console.error('Load SMS settings error:', error);
      }
    };
    
    if (BACKEND_URL) {
      loadPDFTemplate();
      loadCounselingDates();
      loadSMSSettings();
      loadColleges();
      loadCollegeStats();
    }
  }, [BACKEND_URL]);

  const handleSaveCounselingDates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/counseling-dates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(counselingDates)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "Counseling dates saved successfully" });
      } else {
        toast({ 
          title: "Error", 
          description: data.message || "Failed to save counseling dates",
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('Save counseling dates error:', error);
      toast({ 
        title: "Error", 
        description: "Failed to save counseling dates",
        variant: "destructive" 
      });
    }
  };

  const handleSaveSMSSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sms-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(smsSettings)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "SMS settings saved successfully" });
      } else {
        toast({ 
          title: "Error", 
          description: data.message || "Failed to save SMS settings",
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('Save SMS settings error:', error);
      toast({ 
        title: "Error", 
        description: "Failed to save SMS settings",
        variant: "destructive" 
      });
    }
  };

  const handleTestSMS = async (gateway) => {
    try {
      const testMobile = prompt('Enter mobile number for test SMS:');
      if (!testMobile) return;

      const response = await fetch(`${BACKEND_URL}/api/admin/test-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gateway, mobile: testMobile })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: data.message });
      } else {
        toast({ 
          title: "Error", 
          description: data.message || "Test SMS failed",
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('Test SMS error:', error);
      toast({ 
        title: "Error", 
        description: "Failed to send test SMS",
        variant: "destructive" 
      });
    }
  };

  const handleSaveSMTP = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          host: smtpSettings.host,
          port: smtpSettings.port,
          user: smtpSettings.user,
          password: smtpSettings.password,
          from_name: smtpSettings.fromName,
          from_email: smtpSettings.user,
          use_tls: true
        })
      });
      
      if (response.ok) {
        toast({ title: "Success", description: "SMTP settings saved successfully" });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save SMTP settings", variant: "destructive" });
    }
  };

  const handleTestSMTP = async () => {
    const testEmail = prompt("Enter email address to send test email:");
    if (!testEmail) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp/test?test_email=${testEmail}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast({ title: "Success", description: `Test email sent to ${testEmail}` });
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send test email');
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to send test email. Check your SMTP settings.", 
        variant: "destructive" 
      });
    }
  };

  const handleSaveReferralSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          min_withdrawal_amount: cmsContent.referral.minWithdrawalAmount || 1000
        })
      });
      
      if (response.ok) {
        toast({ title: "Success", description: "Referral settings saved successfully" });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save referral settings", variant: "destructive" });
    }
  };

  const handleAddCoupon = () => {
    if (!newCoupon.code || !newCoupon.discount) {
      toast({ title: "Error", description: "Please fill all coupon details", variant: "destructive" });
      return;
    }
    
    const coupon = {
      id: Date.now(),
      ...newCoupon,
      discount: parseFloat(newCoupon.discount),
      active: true
    };
    
    setCoupons([...coupons, coupon]);
    setNewCoupon({ code: '', discount: '', type: 'flat' });
    toast({ title: "Success", description: "Coupon added successfully" });
  };

  const handleToggleCoupon = (id) => {
    setCoupons(coupons.map(c => 
      c.id === id ? { ...c, active: !c.active } : c
    ));
    toast({ title: "Updated", description: "Coupon status updated" });
  };

  const handleDeleteCoupon = (id) => {
    setCoupons(coupons.filter(c => c.id !== id));
    toast({ title: "Deleted", description: "Coupon deleted successfully" });
  };

  const handleEditCollege = (college) => {
    setEditingCollege({...college});
    setEditCollegeModal(true);
  };

  const handleSaveCollege = () => {
    toast({ title: "Success", description: `${editingCollege.name} updated successfully` });
    setEditCollegeModal(false);
    setEditingCollege(null);
  };

  const handleDeleteCollege = (collegeName) => {
    toast({ title: "Deleted", description: `${collegeName} removed from database` });
  };

  const handleEditBranch = (college, branch, branchIndex) => {
    setEditingBranch({ college, branch, branchIndex });
    setEditBranchModal(true);
  };

  // Filter colleges based on search and filters
  const filterColleges = (colleges) => {
    return colleges.filter(college => {
      // Search term filter (college name)
      const matchesSearch = college.name.toLowerCase().includes(collegeSearchTerm.toLowerCase());
      
      // State filter
      const matchesState = !collegeFilters.state || 
        college.state.toLowerCase().includes(collegeFilters.state.toLowerCase());
      
      // City filter
      const matchesCity = !collegeFilters.city || 
        college.city.toLowerCase().includes(collegeFilters.city.toLowerCase());
      
      // Check if college has branches matching fees and rank filters
      let matchesFees = true;
      let matchesRank = true;
      
      if (collegeFilters.minFees || collegeFilters.maxFees || collegeFilters.minRank || collegeFilters.maxRank) {
        const hasBranchMatchingFilters = college.branches.some(branch => {
          // Fees filter
          const branchFees = branch.fees || 0;
          const minFees = collegeFilters.minFees ? parseFloat(collegeFilters.minFees) : 0;
          const maxFees = collegeFilters.maxFees ? parseFloat(collegeFilters.maxFees) : Infinity;
          const feesMatch = branchFees >= minFees && branchFees <= maxFees;
          
          // Rank filter (check all cutoffs)
          let rankMatch = true;
          if (collegeFilters.minRank || collegeFilters.maxRank) {
            const minRank = collegeFilters.minRank ? parseInt(collegeFilters.minRank) : 0;
            const maxRank = collegeFilters.maxRank ? parseInt(collegeFilters.maxRank) : Infinity;
            
            // Check JEE Main cutoffs
            const jeeMainCutoffs = branch.cutoffs?.jeeMain?.[2024] || {};
            const jeeAdvancedCutoffs = branch.cutoffs?.jeeAdvanced?.[2024] || {};
            
            const allCutoffs = [
              ...Object.values(jeeMainCutoffs),
              ...Object.values(jeeAdvancedCutoffs)
            ].filter(cutoff => typeof cutoff === 'number');
            
            if (allCutoffs.length > 0) {
              rankMatch = allCutoffs.some(cutoff => cutoff >= minRank && cutoff <= maxRank);
            } else {
              rankMatch = false;
            }
          }
          
          return feesMatch && rankMatch;
        });
        
        matchesFees = hasBranchMatchingFilters;
        matchesRank = hasBranchMatchingFilters;
      }
      
      return matchesSearch && matchesState && matchesCity && matchesFees && matchesRank;
    });
  };

  const resetCollegeFilters = () => {
    setCollegeSearchTerm('');
    setCollegeFilters({
      state: '',
      city: '',
      minFees: '',
      maxFees: '',
      minRank: '',
      maxRank: ''
    });
  };

  const handleSaveBranch = () => {
    toast({ title: "Success", description: "Branch updated successfully" });
    setEditBranchModal(false);
    setEditingBranch(null);
  };

  const stats = [
    { label: 'Total Users', value: '1,234', icon: Users, color: 'bg-blue-100 text-blue-600' },
    { label: 'Total Revenue', value: '₹3,68,766', icon: DollarSign, color: 'bg-green-100 text-green-600' },
    { label: 'Predictions Today', value: '45', icon: TrendingUp, color: 'bg-purple-100 text-purple-600' },
    { label: 'Total Referrals', value: '89', icon: Users, color: 'bg-orange-100 text-orange-600' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Admin Panel</h1>
                <p className="text-xs text-slate-600">JEE Predictor Pro</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/')}>View Site</Button>
              <Button variant="destructive" onClick={handleLogout} className="gap-2">
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4">
            <TabsList className="inline-flex flex-nowrap min-w-max gap-1">
            <TabsTrigger value="dashboard">Analytics</TabsTrigger>
            <TabsTrigger value="leads">Leads</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="referrals">Referrals</TabsTrigger>
            <TabsTrigger value="withdrawals">Withdrawals</TabsTrigger>
            <TabsTrigger value="payment">Payment</TabsTrigger>
            <TabsTrigger value="paywall">Paywall</TabsTrigger>
            <TabsTrigger value="test-series">Test Series</TabsTrigger>
            <TabsTrigger value="forum">Forum</TabsTrigger>
            <TabsTrigger value="form-guide">Form Guide</TabsTrigger>
            <TabsTrigger value="cms">CMS Editor</TabsTrigger>
            <TabsTrigger value="footer">Footer</TabsTrigger>
            <TabsTrigger value="coupons">Coupons</TabsTrigger>
            <TabsTrigger value="colleges">Colleges</TabsTrigger>
            <TabsTrigger value="pdf">PDF Template</TabsTrigger>
            <TabsTrigger value="counseling">Counseling Dates</TabsTrigger>
            <TabsTrigger value="counseling-requests">Counseling Requests</TabsTrigger>
            <TabsTrigger value="verification">Verification</TabsTrigger>
            <TabsTrigger value="email-provider">Email Provider</TabsTrigger>
            <TabsTrigger value="sms">SMS Gateway</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
          </div>

          {/* Analytics Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            <AnalyticsDashboard />
          </TabsContent>

          {/* Users Tab */}
          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <UserManagement />
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="space-y-6">
            <ReferralManagement />
          </TabsContent>

          {/* Withdrawals Tab */}
          <TabsContent value="withdrawals" className="space-y-6">
            <WithdrawalRequests />
          </TabsContent>

          {/* Payment Gateway Settings Tab */}
          <TabsContent value="payment" className="space-y-6">
            <PaymentGatewaySettings />
          </TabsContent>

          {/* Paywall Settings Tab */}
          <TabsContent value="paywall" className="space-y-6">
            <PaywallAdmin />
          </TabsContent>

          {/* Leads Management Tab */}
          <TabsContent value="leads" className="space-y-6">
            <LeadsAdmin />
          </TabsContent>

          {/* Test Series Admin Tab */}
          <TabsContent value="test-series" className="space-y-6">
            <TestSeriesAdmin />
          </TabsContent>

          {/* Forum Admin Tab */}
          <TabsContent value="forum" className="space-y-6">
            <ForumAdmin />
          </TabsContent>

          {/* Form Guide Admin Tab */}
          <TabsContent value="form-guide" className="space-y-6">
            <FormInstructionsAdmin />
          </TabsContent>

          {/* CMS Editor Tab */}
          <TabsContent value="cms" className="space-y-6">
            <CMSPageEditor BACKEND_URL={BACKEND_URL} />
          </TabsContent>

          {/* Footer Editor Tab */}
          <TabsContent value="footer" className="space-y-6">
            <FooterEditor BACKEND_URL={BACKEND_URL} />
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Add New Discount Coupon</CardTitle>
                <CardDescription>Create discount coupons for users</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Coupon Code</Label>
                    <Input
                      placeholder="e.g., SAVE50"
                      value={newCoupon.code}
                      onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Discount Amount</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 50"
                      value={newCoupon.discount}
                      onChange={(e) => setNewCoupon({...newCoupon, discount: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Discount Type</Label>
                    <select
                      value={newCoupon.type}
                      onChange={(e) => setNewCoupon({...newCoupon, type: e.target.value})}
                      className="w-full h-10 px-3 border border-slate-300 rounded-md"
                    >
                      <option value="flat">Flat (₹)</option>
                      <option value="percentage">Percentage (%)</option>
                    </select>
                  </div>
                </div>
                <Button onClick={handleAddCoupon} className="gap-2">
                  <Save className="w-4 h-4" />
                  Add Coupon
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Active Discount Coupons</CardTitle>
                <CardDescription>Manage existing discount coupons</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {coupons.map((coupon) => (
                    <div key={coupon.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="font-mono font-bold text-lg bg-blue-100 text-blue-800 px-3 py-1 rounded">
                          {coupon.code}
                        </div>
                        <div>
                          <p className="font-medium">
                            {coupon.type === 'flat' ? '₹' : ''}{coupon.discount}{coupon.type === 'percentage' ? '%' : ''} OFF
                          </p>
                          <p className="text-sm text-slate-600">
                            {coupon.type === 'flat' ? 'Flat Discount' : 'Percentage Discount'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleToggleCoupon(coupon.id)}
                          variant={coupon.active ? "default" : "outline"}
                          size="sm"
                        >
                          {coupon.active ? 'Active' : 'Inactive'}
                        </Button>
                        <Button
                          onClick={() => handleDeleteCoupon(coupon.id)}
                          variant="destructive"
                          size="sm"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Colleges Tab */}
          <TabsContent value="colleges" className="space-y-6">
            {/* Statistics Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>College Database Statistics</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={handleDownloadCSV}
                  >
                    <FileDown className="w-4 h-4" />
                    Download CSV
                  </Button>
                </CardTitle>
                <CardDescription>Click on any category to filter colleges</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-6 gap-3">
                  <div 
                    className="text-center p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors border-2 border-transparent hover:border-blue-300"
                    onClick={() => handleStatCardClick('IIT')}
                  >
                    <p className="text-xl font-bold text-blue-600">{collegeStats.iit}</p>
                    <p className="text-xs text-slate-600">IIT</p>
                  </div>
                  <div 
                    className="text-center p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors border-2 border-transparent hover:border-green-300"
                    onClick={() => handleStatCardClick('NIT')}
                  >
                    <p className="text-xl font-bold text-green-600">{collegeStats.nit}</p>
                    <p className="text-xs text-slate-600">NIT</p>
                  </div>
                  <div 
                    className="text-center p-3 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors border-2 border-transparent hover:border-purple-300"
                    onClick={() => handleStatCardClick('IIIT')}
                  >
                    <p className="text-xl font-bold text-purple-600">{collegeStats.iiit}</p>
                    <p className="text-xs text-slate-600">IIIT</p>
                  </div>
                  <div 
                    className="text-center p-3 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors border-2 border-transparent hover:border-yellow-300"
                    onClick={() => handleStatCardClick('GFTI')}
                  >
                    <p className="text-xl font-bold text-yellow-600">{collegeStats.gfti}</p>
                    <p className="text-xs text-slate-600">GFTI</p>
                  </div>
                  <div 
                    className="text-center p-3 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors border-2 border-transparent hover:border-orange-300"
                    onClick={() => handleStatCardClick('JEE-Rank-Based-Admissions')}
                  >
                    <p className="text-xl font-bold text-orange-600">{collegeStats.jee_ranked}</p>
                    <p className="text-xs text-slate-600">JEE Ranked</p>
                  </div>
                  <div 
                    className="text-center p-3 bg-slate-50 rounded-lg border-2 border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => {
                      setCollegeTypeFilter('');
                      setCollegePage(1);
                      loadColleges(1, collegesPerPage, '', '', '');
                    }}
                  >
                    <p className="text-xl font-bold text-slate-700">{collegeStats.total}</p>
                    <p className="text-xs text-slate-600">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Import Generated Colleges */}
            <Card>
              <CardHeader>
                <CardTitle>Import 3000 JEE Colleges</CardTitle>
                <CardDescription>Import pre-generated 3000 colleges with realistic data</CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="gap-2"
                  onClick={async () => {
                    try {
                      const response = await fetch(`${BACKEND_URL}/api/admin/colleges/import-json`, {
                        method: 'POST'
                      });
                      const data = await response.json();
                      if (data.success) {
                        toast({ title: "Success", description: data.message });
                      }
                    } catch (error) {
                      toast({ title: "Error", description: "Failed to import colleges", variant: "destructive" });
                    }
                  }}
                >
                  <Database className="w-4 h-4" />
                  Import 3000 Colleges to Database
                </Button>
                <p className="text-sm text-slate-600 mt-2">
                  Includes Government, Deemed, and Private colleges from all Indian states
                </p>
              </CardContent>
            </Card>

            {/* CSV Import Section */}
            <Card>
              <CardHeader>
                <CardTitle>Import Colleges from CSV</CardTitle>
                <CardDescription>Bulk upload additional colleges using CSV file</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Select CSV File</Label>
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const text = await file.text();
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/admin/colleges/import-csv`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ file_content: text })
                            });
                            const data = await response.json();
                            if (data.success) {
                              toast({ title: "Success", description: data.message });
                            }
                          } catch (error) {
                            toast({ title: "Error", description: "CSV import failed", variant: "destructive" });
                          }
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900 mb-2">CSV Format Required:</p>
                  <code className="text-xs text-blue-800">
                    name,type,state,city,min_fee,max_fee,established,accreditation,website
                  </code>
                  <p className="text-xs text-slate-600 mt-2">
                    Type: Government, Deemed, or Private
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Add New College */}
            <Card>
              <CardHeader>
                <CardTitle>Add New College Manually</CardTitle>
                <CardDescription>Add a single college with details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>College Name</Label>
                    <Input 
                      placeholder="e.g., ABC Institute of Technology" 
                      value={editingCollege?.name || ''}
                      onChange={(e) => setEditingCollege({...editingCollege, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <select 
                      className="w-full h-10 px-3 border border-slate-300 rounded-md"
                      value={editingCollege?.type || 'Private'}
                      onChange={(e) => setEditingCollege({...editingCollege, type: e.target.value})}
                    >
                      <option value="Government">Government</option>
                      <option value="Deemed">Deemed University</option>
                      <option value="Private">Private</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input 
                      placeholder="e.g., Maharashtra" 
                      value={editingCollege?.state || ''}
                      onChange={(e) => setEditingCollege({...editingCollege, state: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input 
                      placeholder="e.g., Mumbai" 
                      value={editingCollege?.city || ''}
                      onChange={(e) => setEditingCollege({...editingCollege, city: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Fee (₹)</Label>
                    <Input 
                      type="number" 
                      placeholder="e.g., 200000"
                      value={editingCollege?.fees?.min || ''}
                      onChange={(e) => setEditingCollege({
                        ...editingCollege, 
                        fees: {...editingCollege?.fees, min: parseInt(e.target.value)}
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Fee (₹)</Label>
                    <Input 
                      type="number" 
                      placeholder="e.g., 500000"
                      value={editingCollege?.fees?.max || ''}
                      onChange={(e) => setEditingCollege({
                        ...editingCollege, 
                        fees: {...editingCollege?.fees, max: parseInt(e.target.value)}
                      })}
                    />
                  </div>
                </div>
                <Button 
                  className="gap-2"
                  onClick={async () => {
                    try {
                      const response = await fetch(`${BACKEND_URL}/api/admin/colleges/add`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          ...editingCollege,
                          category: 'JEE-Rank-Based-Admissions',
                          branches: [],
                          fees: editingCollege?.fees || {min: 0, max: 0}
                        })
                      });
                      const data = await response.json();
                      if (data.success) {
                        toast({ title: "Success", description: "College added successfully" });
                        setEditingCollege(null);
                      }
                    } catch (error) {
                      toast({ title: "Error", description: "Failed to add college", variant: "destructive" });
                    }
                  }}
                >
                  <Database className="w-4 h-4" />
                  Add College
                </Button>
              </CardContent>
            </Card>

            {/* Search and Filter Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Search & Filter Colleges
                </CardTitle>
                <CardDescription>Find colleges by name, location, fees, and cutoff ranks</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search Box */}
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by college name..."
                    value={collegeSearchTerm}
                    onChange={(e) => setCollegeSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Filter Grid */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">State</Label>
                    <Input
                      placeholder="e.g., Delhi, Maharashtra"
                      value={collegeFilters.state}
                      onChange={(e) => setCollegeFilters({ ...collegeFilters, state: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">City</Label>
                    <Input
                      placeholder="e.g., Mumbai, Bangalore"
                      value={collegeFilters.city}
                      onChange={(e) => setCollegeFilters({ ...collegeFilters, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Fees Range (₹)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={collegeFilters.minFees}
                        onChange={(e) => setCollegeFilters({ ...collegeFilters, minFees: e.target.value })}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={collegeFilters.maxFees}
                        onChange={(e) => setCollegeFilters({ ...collegeFilters, maxFees: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm font-medium">Cutoff Rank Range</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min Rank"
                        value={collegeFilters.minRank}
                        onChange={(e) => setCollegeFilters({ ...collegeFilters, minRank: e.target.value })}
                      />
                      <Input
                        type="number"
                        placeholder="Max Rank"
                        value={collegeFilters.maxRank}
                        onChange={(e) => setCollegeFilters({ ...collegeFilters, maxRank: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      variant="outline" 
                      className="w-full gap-2"
                      onClick={resetCollegeFilters}
                    >
                      <X className="w-4 h-4" />
                      Clear Filters
                    </Button>
                  </div>
                </div>

                {/* Active Filters Display */}
                {(collegeSearchTerm || collegeFilters.state || collegeFilters.city || 
                  collegeFilters.minFees || collegeFilters.maxFees || 
                  collegeFilters.minRank || collegeFilters.maxRank) && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    <span className="text-sm text-slate-600 font-medium">Active Filters:</span>
                    {collegeSearchTerm && (
                      <Badge variant="secondary" className="gap-1">
                        Name: {collegeSearchTerm}
                        <X 
                          className="w-3 h-3 cursor-pointer" 
                          onClick={() => setCollegeSearchTerm('')}
                        />
                      </Badge>
                    )}
                    {collegeFilters.state && (
                      <Badge variant="secondary" className="gap-1">
                        State: {collegeFilters.state}
                        <X 
                          className="w-3 h-3 cursor-pointer" 
                          onClick={() => setCollegeFilters({ ...collegeFilters, state: '' })}
                        />
                      </Badge>
                    )}
                    {collegeFilters.city && (
                      <Badge variant="secondary" className="gap-1">
                        City: {collegeFilters.city}
                        <X 
                          className="w-3 h-3 cursor-pointer" 
                          onClick={() => setCollegeFilters({ ...collegeFilters, city: '' })}
                        />
                      </Badge>
                    )}
                    {(collegeFilters.minFees || collegeFilters.maxFees) && (
                      <Badge variant="secondary" className="gap-1">
                        Fees: ₹{collegeFilters.minFees || '0'} - ₹{collegeFilters.maxFees || '∞'}
                        <X 
                          className="w-3 h-3 cursor-pointer" 
                          onClick={() => setCollegeFilters({ ...collegeFilters, minFees: '', maxFees: '' })}
                        />
                      </Badge>
                    )}
                    {(collegeFilters.minRank || collegeFilters.maxRank) && (
                      <Badge variant="secondary" className="gap-1">
                        Rank: {collegeFilters.minRank || '0'} - {collegeFilters.maxRank || '∞'}
                        <X 
                          className="w-3 h-3 cursor-pointer" 
                          onClick={() => setCollegeFilters({ ...collegeFilters, minRank: '', maxRank: '' })}
                        />
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Filters Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filters & Display Options
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-6 gap-4">
                  {/* Type Filter */}
                  <div className="space-y-2">
                    <Label>College Type</Label>
                    <select
                      className="w-full h-10 px-3 border border-slate-300 rounded-md"
                      value={collegeTypeFilter}
                      onChange={(e) => {
                        setCollegeTypeFilter(e.target.value);
                        setCollegePage(1);
                        loadColleges(1, collegesPerPage, e.target.value, collegeStateFilter, collegeCityFilter);
                      }}
                    >
                      <option value="">All Types</option>
                      <option value="IIT">IIT</option>
                      <option value="NIT">NIT</option>
                      <option value="IIIT">IIIT</option>
                      <option value="GFTI">GFTI</option>
                      <option value="JEE-Rank-Based-Admissions">JEE Ranked Colleges</option>
                    </select>
                  </div>

                  {/* Ranking Filter */}
                  <div className="space-y-2">
                    <Label>Accreditation/Ranking</Label>
                    <select
                      className="w-full h-10 px-3 border border-slate-300 rounded-md"
                      onChange={(e) => {
                        setCollegePage(1);
                        loadColleges(1, collegesPerPage, collegeTypeFilter, collegeStateFilter, collegeCityFilter, e.target.value);
                      }}
                    >
                      <option value="">All Accreditations</option>
                      <option value="NAAC A++">NAAC A++</option>
                      <option value="NAAC A+">NAAC A+</option>
                      <option value="NAAC A">NAAC A</option>
                      <option value="NAAC B+">NAAC B+</option>
                      <option value="NBA">NBA Accredited</option>
                      <option value="NIRF">NIRF Ranked</option>
                    </select>
                  </div>

                  {/* State Filter */}
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      placeholder="e.g., Maharashtra"
                      value={collegeStateFilter}
                      onChange={(e) => {
                        setCollegeStateFilter(e.target.value);
                      }}
                      onBlur={() => {
                        setCollegePage(1);
                        loadColleges(1, collegesPerPage, collegeTypeFilter, collegeStateFilter, collegeCityFilter);
                      }}
                    />
                  </div>

                  {/* City Filter */}
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      placeholder="e.g., Mumbai"
                      value={collegeCityFilter}
                      onChange={(e) => {
                        setCollegeCityFilter(e.target.value);
                      }}
                      onBlur={() => {
                        setCollegePage(1);
                        loadColleges(1, collegesPerPage, collegeTypeFilter, collegeStateFilter, collegeCityFilter);
                      }}
                    />
                  </div>

                  {/* Results Per Page */}
                  <div className="space-y-2">
                    <Label>Results Per Page</Label>
                    <select
                      className="w-full h-10 px-3 border border-slate-300 rounded-md"
                      value={collegesPerPage}
                      onChange={(e) => {
                        const newPerPage = parseInt(e.target.value);
                        setCollegesPerPage(newPerPage);
                        setCollegePage(1);
                        loadColleges(1, newPerPage, collegeTypeFilter, collegeStateFilter, collegeCityFilter);
                      }}
                    >
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                      <option value="250">250</option>
                      <option value="500">500</option>
                      <option value="1000">1000</option>
                      <option value="3000">3000</option>
                    </select>
                  </div>

                  {/* Clear Filters Button */}
                  <div className="space-y-2">
                    <Label>&nbsp;</Label>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setCollegeTypeFilter('');
                        setCollegeStateFilter('');
                        setCollegeCityFilter('');
                        setCollegePage(1);
                        loadColleges(1, collegesPerPage, '', '', '');
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Existing Colleges List */}
            <Card id="colleges-list">
              <CardHeader>
                <CardTitle>
                  Database Colleges ({dbColleges.length} showing)
                </CardTitle>
                <CardDescription>
                  Page {collegePage} of {collegeTotalPages} | Showing {collegesPerPage} per page
                  {collegeTypeFilter && ` | Filtered by: ${collegeTypeFilter}`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {collegesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {dbColleges.length === 0 ? (
                        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                          <Database className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                          <p className="text-slate-600 font-medium">No colleges in database</p>
                          <p className="text-sm text-slate-500 mt-1">Click "Import 3000 Colleges" button above to populate the database</p>
                        </div>
                      ) : (
                        dbColleges.map((college) => (
                      <Card key={college.id} className="border-slate-200">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <CardTitle className="text-lg">{college.name}</CardTitle>
                                <Badge variant="outline" className="text-xs">
                                  {college.type}
                                </Badge>
                              </div>
                              <CardDescription>
                                <span className="font-medium">Location:</span> {college.city}, {college.state}
                              </CardDescription>
                              <CardDescription className="mt-1">
                                <span className="font-medium">Branches:</span> {college.branches?.length || 0}
                              </CardDescription>
                              <CardDescription className="mt-1">
                                <span className="font-medium">Fees:</span> {
                                  college.fees?.min && college.fees?.max
                                    ? `₹${(college.fees.min / 100000).toFixed(1)}L - ₹${(college.fees.max / 100000).toFixed(1)}L/year`
                                    : 'Contact College'
                                }
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="gap-2" onClick={() => handleEditCollege(college)}>
                                <Edit className="w-3 h-3" />
                                Edit
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleDeleteCollege(college.name)}>
                                Delete
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <details className="group">
                            <summary className="cursor-pointer font-medium text-sm text-blue-600 hover:text-blue-700 flex items-center gap-2">
                              <span>View {college.branches.length} Branches & Cutoffs</span>
                              <span className="group-open:rotate-180 transition-transform">▼</span>
                            </summary>
                            <div className="mt-4 space-y-3">
                              {college.branches.map((branch, idx) => (
                                <div key={idx} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                                  <div className="flex justify-between items-start mb-3">
                                    <div>
                                      <p className="font-medium text-slate-900">{branch.name}</p>
                                      <p className="text-sm text-slate-600">Code: {branch.code}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm text-slate-600">
                                        Fees: {
                                          typeof branch.fees === 'number' 
                                            ? `₹${(branch.fees / 100000).toFixed(1)}L/year`
                                            : branch.fees?.min && branch.fees?.max
                                              ? `₹${(branch.fees.min / 100000).toFixed(1)}L - ₹${(branch.fees.max / 100000).toFixed(1)}L/year`
                                              : 'Not Available'
                                        }
                                      </p>
                                      <p className="text-sm text-slate-600">Seats: {branch.seats || 'N/A'}</p>
                                    </div>
                                  </div>
                                  
                                  {/* Cutoffs Display */}
                                  <div className="space-y-2">
                                    {/* Show cutoff_ranks for JEE-Rank-Based colleges */}
                                    {branch.cutoff_ranks && (
                                      <div className="mt-3 bg-slate-50 p-3 rounded">
                                        <p className="text-xs font-medium text-slate-700 mb-2">JEE Rank Cutoffs (Category-wise):</p>
                                        <div className="grid grid-cols-2 gap-2">
                                          {Object.entries(branch.cutoff_ranks).map(([category, data]) => (
                                            <div key={category} className="text-xs">
                                              <p className="font-medium uppercase text-slate-700">{category}</p>
                                              <p className="text-slate-900 font-bold">Opening: {data.opening}</p>
                                              <p className="text-slate-600 text-xs">Closing: {data.closing}</p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Show JEE Main cutoffs for IIT/NIT/IIIT/GFTI colleges */}
                                    {branch.cutoffs?.jeeMain && !branch.cutoff_ranks && (
                                      <div className="mt-3 bg-blue-50 p-3 rounded">
                                        <p className="text-xs font-medium text-blue-700 mb-2">JEE Main 2024 Cutoffs:</p>
                                        <div className="grid grid-cols-4 gap-2">
                                          {Object.entries(branch.cutoffs.jeeMain[2024] || {}).map(([category, cutoff]) => (
                                            <div key={category} className="text-xs bg-white rounded p-2">
                                              <p className="font-medium uppercase text-slate-700">{category}</p>
                                              <p className="text-slate-900 font-bold">
                                                {typeof cutoff === 'object' ? cutoff?.closing || 'N/A' : cutoff}
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Show JEE Advanced cutoffs for IIT/NIT/IIIT/GFTI colleges */}
                                    {branch.cutoffs?.jeeAdvanced && !branch.cutoff_ranks && (
                                      <div className="mt-2 bg-green-50 p-3 rounded">
                                        <p className="font-medium text-sm text-green-700 mb-2">JEE Advanced 2024 Cutoffs:</p>
                                        <div className="grid grid-cols-4 gap-2 text-xs">
                                          {Object.entries(branch.cutoffs.jeeAdvanced[2024] || {}).map(([category, cutoff]) => (
                                            <div key={category} className="bg-white rounded p-2">
                                              <p className="font-medium uppercase text-slate-700">{category}</p>
                                              <p className="text-slate-900 font-bold">
                                                {typeof cutoff === 'object' ? cutoff?.closing || 'N/A' : cutoff}
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="flex gap-2 mt-3">
                                    <Button size="sm" variant="outline" className="text-xs" onClick={() => handleEditBranch(college, branch, idx)}>
                                      Edit Branch
                                    </Button>
                                    <Button size="sm" variant="outline" className="text-xs" onClick={() => handleEditBranch(college, branch, idx)}>
                                      Update Cutoffs
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        </CardContent>
                      </Card>
                        ))
                      )}
                    </div>
                    
                    {/* Pagination */}
                    {collegeTotalPages > 1 && (
                      <div className="flex justify-center items-center gap-2 mt-6">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={collegePage === 1}
                          onClick={() => {
                            setCollegePage(1);
                            loadColleges(1, collegesPerPage, collegeTypeFilter, collegeStateFilter, collegeCityFilter);
                          }}
                        >
                          First
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={collegePage === 1}
                          onClick={() => {
                            const newPage = collegePage - 1;
                            setCollegePage(newPage);
                            loadColleges(newPage, collegesPerPage, collegeTypeFilter, collegeStateFilter, collegeCityFilter);
                          }}
                        >
                          Previous
                        </Button>
                        <span className="px-4 py-2 text-sm font-medium border rounded">
                          Page {collegePage} of {collegeTotalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={collegePage === collegeTotalPages}
                          onClick={() => {
                            const newPage = collegePage + 1;
                            setCollegePage(newPage);
                            loadColleges(newPage, collegesPerPage, collegeTypeFilter, collegeStateFilter, collegeCityFilter);
                          }}
                        >
                          Next
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={collegePage === collegeTotalPages}
                          onClick={() => {
                            setCollegePage(collegeTotalPages);
                            loadColleges(collegeTotalPages, collegesPerPage, collegeTypeFilter, collegeStateFilter, collegeCityFilter);
                          }}
                        >
                          Last
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PDF Template Tab */}
          <TabsContent value="pdf" className="space-y-6">
            {/* PDF Template Editor */}
            <Card>
              <CardHeader>
                <CardTitle>PDF Report Template Editor</CardTitle>
                <CardDescription>Customize all aspects of the PDF report sent to users</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Template Type Selection */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Template Design</h3>
                  <div className="grid md:grid-cols-4 gap-4">
                    {['professional', 'modern', 'classic', 'minimal'].map((type) => (
                      <div
                        key={type}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          pdfTemplate.template_type === type
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300'
                        }`}
                        onClick={() => setPdfTemplate({ ...pdfTemplate, template_type: type })}
                      >
                        <p className="font-semibold capitalize text-center">{type}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Header & Branding Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Header & Branding</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Main Header Text</Label>
                      <Input
                        value={pdfTemplate.header}
                        onChange={(e) => setPdfTemplate({ ...pdfTemplate, header: e.target.value })}
                        placeholder="e.g., College Kampus - JEE Predictor"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Subheader Text</Label>
                      <Input
                        value={pdfTemplate.subheader}
                        onChange={(e) => setPdfTemplate({ ...pdfTemplate, subheader: e.target.value })}
                        placeholder="e.g., User Prediction Report"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Branding Text</Label>
                      <Input
                        value={pdfTemplate.brandingText}
                        onChange={(e) => setPdfTemplate({ ...pdfTemplate, brandingText: e.target.value })}
                        placeholder="e.g., College Kampus"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Footer Text</Label>
                      <Input
                        value={pdfTemplate.footer}
                        onChange={(e) => setPdfTemplate({ ...pdfTemplate, footer: e.target.value })}
                        placeholder="e.g., © 2024 College Kampus"
                      />
                    </div>
                  </div>
                </div>

                {/* Colors Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Colors</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Header Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={pdfTemplate.headerColor}
                          onChange={(e) => setPdfTemplate({ ...pdfTemplate, headerColor: e.target.value })}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={pdfTemplate.headerColor}
                          onChange={(e) => setPdfTemplate({ ...pdfTemplate, headerColor: e.target.value })}
                          placeholder="#2563eb"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Table Header Background</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={pdfTemplate.tableHeaderBgColor}
                          onChange={(e) => setPdfTemplate({ ...pdfTemplate, tableHeaderBgColor: e.target.value })}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={pdfTemplate.tableHeaderBgColor}
                          onChange={(e) => setPdfTemplate({ ...pdfTemplate, tableHeaderBgColor: e.target.value })}
                          placeholder="#2563eb"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Table Header Text</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={pdfTemplate.tableHeaderTextColor}
                          onChange={(e) => setPdfTemplate({ ...pdfTemplate, tableHeaderTextColor: e.target.value })}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={pdfTemplate.tableHeaderTextColor}
                          onChange={(e) => setPdfTemplate({ ...pdfTemplate, tableHeaderTextColor: e.target.value })}
                          placeholder="#ffffff"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chance Colors Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Admission Chance Colors</h3>
                  <p className="text-sm text-slate-600">Customize colors for different admission chance levels</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>High Chance Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={pdfTemplate.chanceColors?.high || '#22c55e'}
                          onChange={(e) => setPdfTemplate({ 
                            ...pdfTemplate, 
                            chanceColors: { ...pdfTemplate.chanceColors, high: e.target.value }
                          })}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={pdfTemplate.chanceColors?.high || '#22c55e'}
                          onChange={(e) => setPdfTemplate({ 
                            ...pdfTemplate, 
                            chanceColors: { ...pdfTemplate.chanceColors, high: e.target.value }
                          })}
                          placeholder="#22c55e"
                          className="flex-1"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Default: Green (#22c55e)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Medium Chance Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={pdfTemplate.chanceColors?.medium || '#a16207'}
                          onChange={(e) => setPdfTemplate({ 
                            ...pdfTemplate, 
                            chanceColors: { ...pdfTemplate.chanceColors, medium: e.target.value }
                          })}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={pdfTemplate.chanceColors?.medium || '#a16207'}
                          onChange={(e) => setPdfTemplate({ 
                            ...pdfTemplate, 
                            chanceColors: { ...pdfTemplate.chanceColors, medium: e.target.value }
                          })}
                          placeholder="#a16207"
                          className="flex-1"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Default: Brown (#a16207)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Low Chance Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={pdfTemplate.chanceColors?.low || '#ef4444'}
                          onChange={(e) => setPdfTemplate({ 
                            ...pdfTemplate, 
                            chanceColors: { ...pdfTemplate.chanceColors, low: e.target.value }
                          })}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={pdfTemplate.chanceColors?.low || '#ef4444'}
                          onChange={(e) => setPdfTemplate({ 
                            ...pdfTemplate, 
                            chanceColors: { ...pdfTemplate.chanceColors, low: e.target.value }
                          })}
                          placeholder="#ef4444"
                          className="flex-1"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Default: Red (#ef4444)</p>
                    </div>
                  </div>
                </div>

                {/* Font Sizes Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Font Sizes</h3>
                  <div className="grid md:grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Label>Header Font Size</Label>
                      <Input
                        type="number"
                        min="12"
                        max="24"
                        value={pdfTemplate.fontSize?.header || 18}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          fontSize: { ...pdfTemplate.fontSize, header: parseInt(e.target.value) || 18 }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Subheader Font Size</Label>
                      <Input
                        type="number"
                        min="10"
                        max="16"
                        value={pdfTemplate.fontSize?.subheader || 12}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          fontSize: { ...pdfTemplate.fontSize, subheader: parseInt(e.target.value) || 12 }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Body Font Size</Label>
                      <Input
                        type="number"
                        min="8"
                        max="14"
                        value={pdfTemplate.fontSize?.body || 10}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          fontSize: { ...pdfTemplate.fontSize, body: parseInt(e.target.value) || 10 }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Table Font Size</Label>
                      <Input
                        type="number"
                        min="6"
                        max="12"
                        value={pdfTemplate.fontSize?.table || 8}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          fontSize: { ...pdfTemplate.fontSize, table: parseInt(e.target.value) || 8 }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Chance Font Size</Label>
                      <Input
                        type="number"
                        min="6"
                        max="14"
                        value={pdfTemplate.fontSize.chance}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          fontSize: { ...pdfTemplate.fontSize, chance: parseInt(e.target.value) || 9 }
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Table Columns Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Table Columns</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.tableColumns?.serialNumber ?? true}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          tableColumns: { ...pdfTemplate.tableColumns, serialNumber: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Serial Number</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.tableColumns?.collegeName ?? true}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          tableColumns: { ...pdfTemplate.tableColumns, collegeName: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">College Name</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.tableColumns?.branch ?? true}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          tableColumns: { ...pdfTemplate.tableColumns, branch: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Branch</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.tableColumns?.location ?? true}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          tableColumns: { ...pdfTemplate.tableColumns, location: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Location</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.tableColumns?.cutoffRank ?? true}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          tableColumns: { ...pdfTemplate.tableColumns, cutoffRank: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Cutoff Rank</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.tableColumns?.fees ?? true}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          tableColumns: { ...pdfTemplate.tableColumns, fees: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Fees</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.tableColumns?.chance ?? true}
                        onChange={(e) => setPdfTemplate({ 
                          ...pdfTemplate, 
                          tableColumns: { ...pdfTemplate.tableColumns, chance: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Admission Chance</span>
                    </label>
                  </div>
                </div>

                {/* Content Sections */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Content Sections</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.includeUserInfo}
                        onChange={(e) => setPdfTemplate({ ...pdfTemplate, includeUserInfo: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Include User Information</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.includeExamDetails}
                        onChange={(e) => setPdfTemplate({ ...pdfTemplate, includeExamDetails: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Include Exam Details</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.includeHistoricalData}
                        onChange={(e) => setPdfTemplate({ ...pdfTemplate, includeHistoricalData: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Include Historical Data</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pdfTemplate.includeCutoffTrends}
                        onChange={(e) => setPdfTemplate({ ...pdfTemplate, includeCutoffTrends: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Include Cutoff Trends</span>
                    </label>
                  </div>
                </div>

                {/* PDF Styling Options */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">PDF Styling Options</h3>
                  
                  {/* Global Styling */}
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-sm">Global Settings</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Background Color</Label>
                        <Input
                          type="color"
                          value={pdfTemplate.globalBackgroundColor || '#ffffff'}
                          onChange={(e) => setPdfTemplate({ ...pdfTemplate, globalBackgroundColor: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Border Style</Label>
                        <select 
                          className="w-full p-2 border rounded"
                          value={pdfTemplate.globalBorderStyle || 'solid'}
                          onChange={(e) => setPdfTemplate({ ...pdfTemplate, globalBorderStyle: e.target.value })}
                        >
                          <option value="solid">Solid</option>
                          <option value="dashed">Dashed</option>
                          <option value="dotted">Dotted</option>
                          <option value="none">None</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Border Thickness (px)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="5"
                          value={pdfTemplate.globalBorderThickness || 1}
                          onChange={(e) => setPdfTemplate({ ...pdfTemplate, globalBorderThickness: parseInt(e.target.value) || 1 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Border Color</Label>
                        <Input
                          type="color"
                          value={pdfTemplate.globalBorderColor || '#cccccc'}
                          onChange={(e) => setPdfTemplate({ ...pdfTemplate, globalBorderColor: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section-Specific Styling */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">Section-Specific Styling</h4>
                    
                    {/* Header Section */}
                    <div className="p-3 bg-white rounded border">
                      <p className="font-medium text-xs mb-2">Header Section</p>
                      <div className="grid md:grid-cols-4 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Background</Label>
                          <Input
                            type="color"
                            value={pdfTemplate.sectionStyles?.header?.backgroundColor || '#ffffff'}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                header: { ...pdfTemplate.sectionStyles.header, backgroundColor: e.target.value }
                              }
                            })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Border Style</Label>
                          <select 
                            className="w-full p-1 border rounded text-xs"
                            value={pdfTemplate.sectionStyles?.header?.borderStyle || 'solid'}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                header: { ...pdfTemplate.sectionStyles.header, borderStyle: e.target.value }
                              }
                            })}
                          >
                            <option value="solid">Solid</option>
                            <option value="dashed">Dashed</option>
                            <option value="dotted">Dotted</option>
                            <option value="none">None</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Thickness</Label>
                          <Input
                            type="number"
                            min="0"
                            max="5"
                            className="text-xs"
                            value={pdfTemplate.sectionStyles?.header?.borderThickness || 1}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                header: { ...pdfTemplate.sectionStyles.header, borderThickness: parseInt(e.target.value) || 1 }
                              }
                            })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Border Color</Label>
                          <Input
                            type="color"
                            value={pdfTemplate.sectionStyles?.header?.borderColor || '#cccccc'}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                header: { ...pdfTemplate.sectionStyles.header, borderColor: e.target.value }
                              }
                            })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* User Info Section */}
                    <div className="p-3 bg-white rounded border">
                      <p className="font-medium text-xs mb-2">User Info Section</p>
                      <div className="grid md:grid-cols-4 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Background</Label>
                          <Input
                            type="color"
                            value={pdfTemplate.sectionStyles?.userInfo?.backgroundColor || '#f8f9fa'}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                userInfo: { ...pdfTemplate.sectionStyles.userInfo, backgroundColor: e.target.value }
                              }
                            })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Border Style</Label>
                          <select 
                            className="w-full p-1 border rounded text-xs"
                            value={pdfTemplate.sectionStyles?.userInfo?.borderStyle || 'solid'}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                userInfo: { ...pdfTemplate.sectionStyles.userInfo, borderStyle: e.target.value }
                              }
                            })}
                          >
                            <option value="solid">Solid</option>
                            <option value="dashed">Dashed</option>
                            <option value="dotted">Dotted</option>
                            <option value="none">None</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Thickness</Label>
                          <Input
                            type="number"
                            min="0"
                            max="5"
                            className="text-xs"
                            value={pdfTemplate.sectionStyles?.userInfo?.borderThickness || 1}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                userInfo: { ...pdfTemplate.sectionStyles.userInfo, borderThickness: parseInt(e.target.value) || 1 }
                              }
                            })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Border Color</Label>
                          <Input
                            type="color"
                            value={pdfTemplate.sectionStyles?.userInfo?.borderColor || '#dee2e6'}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                userInfo: { ...pdfTemplate.sectionStyles.userInfo, borderColor: e.target.value }
                              }
                            })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Custom Sections Styling */}
                    <div className="p-3 bg-white rounded border">
                      <p className="font-medium text-xs mb-2">Custom Sections Default Styling</p>
                      <div className="grid md:grid-cols-4 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Background</Label>
                          <Input
                            type="color"
                            value={pdfTemplate.sectionStyles?.customSections?.backgroundColor || '#f8f9fa'}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                customSections: { ...pdfTemplate.sectionStyles.customSections, backgroundColor: e.target.value }
                              }
                            })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Border Style</Label>
                          <select 
                            className="w-full p-1 border rounded text-xs"
                            value={pdfTemplate.sectionStyles?.customSections?.borderStyle || 'solid'}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                customSections: { ...pdfTemplate.sectionStyles.customSections, borderStyle: e.target.value }
                              }
                            })}
                          >
                            <option value="solid">Solid</option>
                            <option value="dashed">Dashed</option>
                            <option value="dotted">Dotted</option>
                            <option value="none">None</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Thickness</Label>
                          <Input
                            type="number"
                            min="0"
                            max="5"
                            className="text-xs"
                            value={pdfTemplate.sectionStyles?.customSections?.borderThickness || 1}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                customSections: { ...pdfTemplate.sectionStyles.customSections, borderThickness: parseInt(e.target.value) || 1 }
                              }
                            })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Border Color</Label>
                          <Input
                            type="color"
                            value={pdfTemplate.sectionStyles?.customSections?.borderColor || '#dee2e6'}
                            onChange={(e) => setPdfTemplate({ 
                              ...pdfTemplate, 
                              sectionStyles: {
                                ...pdfTemplate.sectionStyles,
                                customSections: { ...pdfTemplate.sectionStyles.customSections, borderColor: e.target.value }
                              }
                            })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Custom Sections Management */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b pb-2">
                    <h3 className="text-lg font-semibold">Custom Sections</h3>
                    <Button
                      size="sm"
                      onClick={() => {
                        const newSection = {
                          id: Date.now(),
                          title: 'New Section',
                          content: 'Section content goes here...',
                          enabled: true,
                          order: (pdfTemplate.customSections?.length || 0) + 1
                        };
                        setPdfTemplate({
                          ...pdfTemplate,
                          customSections: [...(pdfTemplate.customSections || []), newSection]
                        });
                      }}
                    >
                      <Database className="w-4 h-4 mr-2" />
                      Add Section
                    </Button>
                  </div>

                  {pdfTemplate.customSections && pdfTemplate.customSections.length > 0 ? (
                    <div className="space-y-3">
                      {pdfTemplate.customSections.map((section, index) => (
                        <Card key={section.id} className="border-slate-200">
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-500">Section {index + 1}</span>
                                <Switch
                                  checked={section.enabled}
                                  onCheckedChange={(checked) => {
                                    const updated = [...pdfTemplate.customSections];
                                    updated[index].enabled = checked;
                                    setPdfTemplate({ ...pdfTemplate, customSections: updated });
                                  }}
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const updated = [...pdfTemplate.customSections];
                                    updated[index] = { 
                                      ...updated[index], 
                                      editing: !updated[index].editing 
                                    };
                                    setPdfTemplate({ ...pdfTemplate, customSections: updated });
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => {
                                    if (confirm('Delete this section?')) {
                                      const updated = pdfTemplate.customSections.filter((_, i) => i !== index);
                                      setPdfTemplate({ ...pdfTemplate, customSections: updated });
                                    }
                                  }}
                                >
                                  <Database className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {section.editing ? (
                              <div className="space-y-3">
                                <div className="space-y-2">
                                  <Label>Section Title</Label>
                                  <Input
                                    value={section.title}
                                    onChange={(e) => {
                                      const updated = [...pdfTemplate.customSections];
                                      updated[index].title = e.target.value;
                                      setPdfTemplate({ ...pdfTemplate, customSections: updated });
                                    }}
                                    placeholder="Enter section title"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Section Content</Label>
                                  <Textarea
                                    value={section.content}
                                    onChange={(e) => {
                                      const updated = [...pdfTemplate.customSections];
                                      updated[index].content = e.target.value;
                                      setPdfTemplate({ ...pdfTemplate, customSections: updated });
                                    }}
                                    placeholder="Enter section content"
                                    rows={4}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    const updated = [...pdfTemplate.customSections];
                                    updated[index].editing = false;
                                    setPdfTemplate({ ...pdfTemplate, customSections: updated });
                                  }}
                                >
                                  Done Editing
                                </Button>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <p className="font-medium text-sm">{section.title}</p>
                                <p className="text-xs text-slate-600">{section.content}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed">
                      <Database className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-sm text-slate-600">No custom sections added</p>
                      <p className="text-xs text-slate-500 mt-1">Click "Add Section" to create custom sections for your PDF reports</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button onClick={handleSavePDFTemplate} className="gap-2">
                    <Save className="w-4 h-4" />
                    Save Template
                  </Button>
                  <Button variant="outline" onClick={() => setShowPDFPreview(true)} className="gap-2">
                    <FileText className="w-4 h-4" />
                    Preview PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setPdfTemplate({
                        header: 'College Kampus - JEE Predictor',
                        subheader: 'User Prediction Report',
                        footer: '© 2024 College Kampus. All rights reserved.',
                        brandingText: 'College Kampus',
                        includeUserInfo: true,
                        includeExamDetails: true,
                        includeHistoricalData: true,
                        includeCutoffTrends: true,
                        headerColor: '#2563eb',
                        tableHeaderBgColor: '#2563eb',
                        tableHeaderTextColor: '#ffffff',
                        chanceColors: {
                          high: '#22c55e',
                          medium: '#a16207',
                          low: '#ef4444'
                        },
                        tableColumns: {
                          serialNumber: true,
                          collegeName: true,
                          branch: true,
                          location: true,
                          cutoffRank: true,
                          fees: true,
                          chance: true
                        },
                        fontSize: {
                          header: 18,
                          subheader: 12,
                          body: 10,
                          table: 8,
                          chance: 9
                        },
                        customSections: [],
                        globalBackgroundColor: '#ffffff',
                        globalBorderStyle: 'solid',
                        globalBorderThickness: 1,
                        globalBorderColor: '#cccccc',
                        sectionStyles: {
                          header: {
                            backgroundColor: '#ffffff',
                            borderStyle: 'solid',
                            borderThickness: 1,
                            borderColor: '#cccccc'
                          },
                          userInfo: {
                            backgroundColor: '#f8f9fa',
                            borderStyle: 'solid',
                            borderThickness: 1,
                            borderColor: '#dee2e6'
                          },
                          table: {
                            backgroundColor: '#ffffff',
                            borderStyle: 'solid',
                            borderThickness: 1,
                            borderColor: '#cccccc'
                          },
                          customSections: {
                            backgroundColor: '#f8f9fa',
                            borderStyle: 'solid',
                            borderThickness: 1,
                            borderColor: '#dee2e6'
                          },
                          footer: {
                            backgroundColor: '#ffffff',
                            borderStyle: 'none',
                            borderThickness: 0,
                            borderColor: '#cccccc'
                          }
                        }
                      });
                      toast({ title: "Reset", description: "Template reset to default values" });
                    }}
                  >
                    Reset to Default
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Counseling Requests Tab */}
          <TabsContent value="counseling-requests" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Counseling Requests</CardTitle>
                <CardDescription>View and manage student counseling requests</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Button
                    onClick={async () => {
                      setCounselingRequestsLoading(true);
                      try {
                        const response = await axios.get(`${BACKEND_URL}/api/admin/counseling-requests`);
                        setCounselingRequests(response.data.requests || []);
                      } catch (error) {
                        console.error('Failed to load counseling requests:', error);
                        toast({
                          title: "Error",
                          description: "Failed to load counseling requests",
                          variant: "destructive"
                        });
                      } finally {
                        setCounselingRequestsLoading(false);
                      }
                    }}
                  >
                    {counselingRequestsLoading ? 'Loading...' : 'Load Requests'}
                  </Button>
                </div>

                {counselingRequests.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <Users className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 mb-2">No counseling requests yet</p>
                    <p className="text-sm text-slate-500">Students can submit requests from the homepage or their dashboard</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {counselingRequests.map((request) => (
                      <Card key={request.id} className="border-slate-200">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h4 className="font-semibold text-lg">{request.user_name}</h4>
                              <p className="text-sm text-slate-600">{request.user_email}</p>
                              <p className="text-sm text-slate-600">{request.user_mobile}</p>
                            </div>
                            <Badge variant={
                              request.status === 'pending' ? 'secondary' :
                              request.status === 'in_progress' ? 'default' : 'success'
                            }>
                              {request.status}
                            </Badge>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <Label className="text-xs text-slate-600">JEE Rank</Label>
                              <p className="font-medium">{request.jee_rank}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-slate-600">Category</Label>
                              <p className="font-medium">{request.category}</p>
                            </div>
                          </div>

                          {request.preferred_branches && request.preferred_branches.length > 0 && (
                            <div className="mb-4">
                              <Label className="text-xs text-slate-600">Preferred Branches</Label>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {request.preferred_branches.map((branch, idx) => (
                                  <Badge key={idx} variant="outline">{branch}</Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {request.preferred_states && request.preferred_states.length > 0 && (
                            <div className="mb-4">
                              <Label className="text-xs text-slate-600">Preferred States</Label>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {request.preferred_states.map((state, idx) => (
                                  <Badge key={idx} variant="outline">{state}</Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {request.budget && (
                            <div className="mb-4">
                              <Label className="text-xs text-slate-600">Budget</Label>
                              <p className="text-sm">{request.budget}</p>
                            </div>
                          )}

                          {request.additional_notes && (
                            <div className="mb-4">
                              <Label className="text-xs text-slate-600">Additional Notes</Label>
                              <p className="text-sm text-slate-700">{request.additional_notes}</p>
                            </div>
                          )}

                          <div className="flex gap-2 pt-4 border-t">
                            <Button
                              size="sm"
                              variant={request.status === 'in_progress' ? 'secondary' : 'default'}
                              onClick={async () => {
                                try {
                                  await axios.put(`${BACKEND_URL}/api/admin/counseling-requests/${request.id}/status`, {
                                    status: 'in_progress'
                                  });
                                  toast({ title: "Status updated to In Progress" });
                                  // Reload requests
                                  const response = await axios.get(`${BACKEND_URL}/api/admin/counseling-requests`);
                                  setCounselingRequests(response.data.requests || []);
                                } catch (error) {
                                  toast({
                                    title: "Error",
                                    description: "Failed to update status",
                                    variant: "destructive"
                                  });
                                }
                              }}
                            >
                              Mark In Progress
                            </Button>
                            <Button
                              size="sm"
                              variant={request.status === 'completed' ? 'secondary' : 'default'}
                              onClick={async () => {
                                try {
                                  await axios.put(`${BACKEND_URL}/api/admin/counseling-requests/${request.id}/status`, {
                                    status: 'completed'
                                  });
                                  toast({ title: "Status updated to Completed" });
                                  // Reload requests
                                  const response = await axios.get(`${BACKEND_URL}/api/admin/counseling-requests`);
                                  setCounselingRequests(response.data.requests || []);
                                } catch (error) {
                                  toast({
                                    title: "Error",
                                    description: "Failed to update status",
                                    variant: "destructive"
                                  });
                                }
                              }}
                            >
                              Mark Completed
                            </Button>
                          </div>

                          <div className="mt-2 text-xs text-slate-500">
                            Submitted: {new Date(request.created_at).toLocaleString()}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verification Settings Tab */}
          <TabsContent value="verification" className="space-y-6">
            <VerificationSettings />
          </TabsContent>

          {/* Email Provider Settings Tab */}
          <TabsContent value="email-provider" className="space-y-6">
            <EmailProviderSettings />
          </TabsContent>

          {/* SMS Gateway Tab */}

          {/* PDF Template Tab */}
          {/* Counseling Dates Tab - Enhanced */}
          <TabsContent value="counseling" className="space-y-6">
            {/* JEE Counseling Management */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>JEE/JoSAA Counseling Rounds</CardTitle>
                    <CardDescription>Manage JEE Main and Advanced counseling schedule</CardDescription>
                  </div>
                  <Switch
                    checked={counselingDates.jee_counseling?.active}
                    onCheckedChange={(checked) => setCounselingDates({
                      ...counselingDates,
                      jee_counseling: { ...counselingDates.jee_counseling, active: checked }
                    })}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {counselingDates.jee_counseling?.rounds?.map((round, index) => (
                  <Card key={round.id} className="p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold flex items-center gap-2">
                        Round {index + 1}
                        <Switch
                          checked={round.active}
                          onCheckedChange={(checked) => {
                            const updated = [...counselingDates.jee_counseling.rounds];
                            updated[index].active = checked;
                            setCounselingDates({
                              ...counselingDates,
                              jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                            });
                          }}
                        />
                      </h4>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          const updated = counselingDates.jee_counseling.rounds.filter((_, i) => i !== index);
                          setCounselingDates({
                            ...counselingDates,
                            jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                          });
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Round Name</Label>
                        <Input
                          size="sm"
                          placeholder="JoSAA Round 1"
                          value={round.name}
                          onChange={(e) => {
                            const updated = [...counselingDates.jee_counseling.rounds];
                            updated[index].name = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Registration Start</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={round.registration_start}
                          onChange={(e) => {
                            const updated = [...counselingDates.jee_counseling.rounds];
                            updated[index].registration_start = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Registration End</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={round.registration_end}
                          onChange={(e) => {
                            const updated = [...counselingDates.jee_counseling.rounds];
                            updated[index].registration_end = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Choice Filling Start</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={round.choice_filling_start}
                          onChange={(e) => {
                            const updated = [...counselingDates.jee_counseling.rounds];
                            updated[index].choice_filling_start = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Choice Filling End</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={round.choice_filling_end}
                          onChange={(e) => {
                            const updated = [...counselingDates.jee_counseling.rounds];
                            updated[index].choice_filling_end = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Seat Allotment Date</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={round.seat_allotment_date}
                          onChange={(e) => {
                            const updated = [...counselingDates.jee_counseling.rounds];
                            updated[index].seat_allotment_date = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Payment Deadline</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={round.payment_deadline}
                          onChange={(e) => {
                            const updated = [...counselingDates.jee_counseling.rounds];
                            updated[index].payment_deadline = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Reporting Start</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={round.reporting_start}
                          onChange={(e) => {
                            const updated = [...counselingDates.jee_counseling.rounds];
                            updated[index].reporting_start = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Reporting End</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={round.reporting_end}
                          onChange={(e) => {
                            const updated = [...counselingDates.jee_counseling.rounds];
                            updated[index].reporting_end = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              jee_counseling: { ...counselingDates.jee_counseling, rounds: updated }
                            });
                          }}
                        />
                      </div>
                    </div>
                  </Card>
                ))}
                <Button
                  variant="outline"
                  onClick={() => {
                    const newRound = {
                      id: Date.now(),
                      name: `JoSAA Round ${(counselingDates.jee_counseling?.rounds?.length || 0) + 1}`,
                      registration_start: '',
                      registration_end: '',
                      choice_filling_start: '',
                      choice_filling_end: '',
                      seat_allotment_date: '',
                      payment_deadline: '',
                      reporting_start: '',
                      reporting_end: '',
                      active: true
                    };
                    setCounselingDates({
                      ...counselingDates,
                      jee_counseling: {
                        ...counselingDates.jee_counseling,
                        rounds: [...(counselingDates.jee_counseling?.rounds || []), newRound]
                      }
                    });
                  }}
                  className="w-full"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Add JEE Round
                </Button>
              </CardContent>
            </Card>

            {/* State Counseling Management */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>State Counseling</CardTitle>
                    <CardDescription>Manage state-wise counseling information</CardDescription>
                  </div>
                  <Switch
                    checked={counselingDates.state_counseling?.active}
                    onCheckedChange={(checked) => setCounselingDates({
                      ...counselingDates,
                      state_counseling: { ...counselingDates.state_counseling, active: checked }
                    })}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {counselingDates.state_counseling?.states?.map((state, index) => (
                  <Card key={state.id} className="p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold flex items-center gap-2">
                        {state.state_name || `State ${index + 1}`}
                        <Switch
                          checked={state.active}
                          onCheckedChange={(checked) => {
                            const updated = [...counselingDates.state_counseling.states];
                            updated[index].active = checked;
                            setCounselingDates({
                              ...counselingDates,
                              state_counseling: { ...counselingDates.state_counseling, states: updated }
                            });
                          }}
                        />
                      </h4>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          const updated = counselingDates.state_counseling.states.filter((_, i) => i !== index);
                          setCounselingDates({
                            ...counselingDates,
                            state_counseling: { ...counselingDates.state_counseling, states: updated }
                          });
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">State Name</Label>
                        <Input
                          size="sm"
                          placeholder="Maharashtra"
                          value={state.state_name}
                          onChange={(e) => {
                            const updated = [...counselingDates.state_counseling.states];
                            updated[index].state_name = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              state_counseling: { ...counselingDates.state_counseling, states: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Counseling Name</Label>
                        <Input
                          size="sm"
                          placeholder="MHT-CET"
                          value={state.counseling_name}
                          onChange={(e) => {
                            const updated = [...counselingDates.state_counseling.states];
                            updated[index].counseling_name = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              state_counseling: { ...counselingDates.state_counseling, states: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Registration Start</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={state.registration_start}
                          onChange={(e) => {
                            const updated = [...counselingDates.state_counseling.states];
                            updated[index].registration_start = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              state_counseling: { ...counselingDates.state_counseling, states: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Registration End</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={state.registration_end}
                          onChange={(e) => {
                            const updated = [...counselingDates.state_counseling.states];
                            updated[index].registration_end = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              state_counseling: { ...counselingDates.state_counseling, states: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Choice Filling Start</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={state.choice_filling_start}
                          onChange={(e) => {
                            const updated = [...counselingDates.state_counseling.states];
                            updated[index].choice_filling_start = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              state_counseling: { ...counselingDates.state_counseling, states: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Choice Filling End</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={state.choice_filling_end}
                          onChange={(e) => {
                            const updated = [...counselingDates.state_counseling.states];
                            updated[index].choice_filling_end = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              state_counseling: { ...counselingDates.state_counseling, states: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Seat Allotment Date</Label>
                        <Input
                          type="date"
                          size="sm"
                          value={state.seat_allotment_date}
                          onChange={(e) => {
                            const updated = [...counselingDates.state_counseling.states];
                            updated[index].seat_allotment_date = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              state_counseling: { ...counselingDates.state_counseling, states: updated }
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Website URL</Label>
                        <Input
                          size="sm"
                          placeholder="https://example.com"
                          value={state.website_url}
                          onChange={(e) => {
                            const updated = [...counselingDates.state_counseling.states];
                            updated[index].website_url = e.target.value;
                            setCounselingDates({
                              ...counselingDates,
                              state_counseling: { ...counselingDates.state_counseling, states: updated }
                            });
                          }}
                        />
                      </div>
                    </div>
                  </Card>
                ))}
                <Button
                  variant="outline"
                  onClick={() => {
                    const newState = {
                      id: Date.now(),
                      state_name: '',
                      counseling_name: '',
                      registration_start: '',
                      registration_end: '',
                      choice_filling_start: '',
                      choice_filling_end: '',
                      seat_allotment_date: '',
                      website_url: '',
                      active: true
                    };
                    setCounselingDates({
                      ...counselingDates,
                      state_counseling: {
                        ...counselingDates.state_counseling,
                        states: [...(counselingDates.state_counseling?.states || []), newState]
                      }
                    });
                  }}
                  className="w-full"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Add State Counseling
                </Button>
              </CardContent>
            </Card>

            {/* General Notes */}
            <Card>
              <CardHeader>
                <CardTitle>General Information</CardTitle>
                <CardDescription>Important notes for all students</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Add important information for students regarding counseling..."
                  value={counselingDates.general_notes}
                  onChange={(e) => setCounselingDates({ ...counselingDates, general_notes: e.target.value })}
                  rows={4}
                />
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={handleSaveCounselingDates} className="gap-2">
                <Save className="w-4 h-4" />
                Save All Counseling Information
              </Button>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Admin Password Change */}
            <Card>
              <CardHeader>
                <CardTitle>Change Admin Password</CardTitle>
                <CardDescription>Update your admin panel password</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-md space-y-4">
                  <div className="space-y-2">
                    <Label>Current Password</Label>
                    <Input
                      type="password"
                      placeholder="Enter current password"
                      value={passwordChange.current_password}
                      onChange={(e) => setPasswordChange({ ...passwordChange, current_password: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>New Password</Label>
                    <Input
                      type="password"
                      placeholder="Enter new password"
                      value={passwordChange.new_password}
                      onChange={(e) => setPasswordChange({ ...passwordChange, new_password: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm New Password</Label>
                    <Input
                      type="password"
                      placeholder="Confirm new password"
                      value={passwordChange.confirm_password}
                      onChange={(e) => setPasswordChange({ ...passwordChange, confirm_password: e.target.value })}
                    />
                  </div>
                  <Button
                    onClick={async () => {
                      if (!passwordChange.current_password || !passwordChange.new_password) {
                        toast({
                          title: "Error",
                          description: "Please fill all fields",
                          variant: "destructive"
                        });
                        return;
                      }
                      if (passwordChange.new_password !== passwordChange.confirm_password) {
                        toast({
                          title: "Error",
                          description: "New passwords do not match",
                          variant: "destructive"
                        });
                        return;
                      }
                      if (passwordChange.new_password.length < 6) {
                        toast({
                          title: "Error",
                          description: "Password must be at least 6 characters",
                          variant: "destructive"
                        });
                        return;
                      }
                      try {
                        const response = await axios.post(`${BACKEND_URL}/api/admin/change-password`, {
                          current_password: passwordChange.current_password,
                          new_password: passwordChange.new_password
                        });
                        if (response.data.success) {
                          toast({
                            title: "Success",
                            description: "Password changed successfully"
                          });
                          setPasswordChange({ current_password: '', new_password: '', confirm_password: '' });
                        }
                      } catch (error) {
                        toast({
                          title: "Error",
                          description: error.response?.data?.detail || "Failed to change password",
                          variant: "destructive"
                        });
                      }
                    }}
                  >
                    Change Password
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* User Roles Management */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>User Roles & Permissions</CardTitle>
                    <CardDescription>Manage user access levels and permissions</CardDescription>
                  </div>
                  <Button
                    onClick={async () => {
                      setRolesLoading(true);
                      try {
                        const response = await axios.get(`${BACKEND_URL}/api/admin/roles`);
                        setUserRoles(response.data.roles || []);
                      } catch (error) {
                        toast({
                          title: "Error",
                          description: "Failed to load roles",
                          variant: "destructive"
                        });
                      } finally {
                        setRolesLoading(false);
                      }
                    }}
                  >
                    {rolesLoading ? 'Loading...' : 'Load Roles'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {userRoles.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Click "Load Roles" to view and manage user roles
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userRoles.map((role) => (
                      <Card key={role.id} className="border-slate-200">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h4 className="font-semibold text-lg">{role.display_name}</h4>
                              <p className="text-sm text-slate-600">Role: {role.name}</p>
                            </div>
                            <Badge variant={role.active ? 'default' : 'secondary'}>
                              {role.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          
                          <div className="mb-4">
                            <Label className="text-xs text-slate-600 mb-2 block">Permissions:</Label>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(role.permissions).map(([key, value]) => (
                                value && (
                                  <Badge key={key} variant="outline" className="text-xs">
                                    {key.replace(/_/g, ' ')}
                                  </Badge>
                                )
                              ))}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingRole(role);
                                setShowRoleDialog(true);
                              }}
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit Permissions
                            </Button>
                            {role.id !== 'role_admin' && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={async () => {
                                  if (confirm(`Delete role "${role.display_name}"?`)) {
                                    try {
                                      await axios.delete(`${BACKEND_URL}/api/admin/roles/${role.id}`);
                                      toast({ title: "Role deleted successfully" });
                                      const response = await axios.get(`${BACKEND_URL}/api/admin/roles`);
                                      setUserRoles(response.data.roles || []);
                                    } catch (error) {
                                      toast({
                                        title: "Error",
                                        description: "Failed to delete role",
                                        variant: "destructive"
                                      });
                                    }
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingRole({
                          name: '',
                          display_name: '',
                          permissions: {
                            dashboard: true,
                            users: false,
                            colleges: false,
                            predictions: false,
                            payments: false,
                            referrals: false,
                            withdrawals: false,
                            cms: false,
                            pdf_template: false,
                            sms_settings: false,
                            payment_settings: false,
                            counseling_dates: false,
                            counseling_requests: false
                          }
                        });
                        setShowRoleDialog(true);
                      }}
                      className="w-full"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Create New Role
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>SMTP Email Settings</CardTitle>
                <CardDescription>Configure email server for sending reports</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SMTP Host</Label>
                    <Input
                      value={smtpSettings.host}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      value={smtpSettings.port}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, port: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP User</Label>
                    <Input
                      value={smtpSettings.user}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, user: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Password</Label>
                    <Input
                      type="password"
                      value={smtpSettings.password}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>From Name</Label>
                    <Input
                      value={smtpSettings.fromName}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, fromName: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveSMTP} className="gap-2">
                    <Mail className="w-4 h-4" />
                    Save SMTP Settings
                  </Button>
                  <Button onClick={handleTestSMTP} variant="outline" className="gap-2">
                    <Mail className="w-4 h-4" />
                    Send Test Email
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Referral & Withdrawal Settings</CardTitle>
                <CardDescription>Configure minimum withdrawal amount for referral earnings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Minimum Withdrawal Amount (₹)</Label>
                  <Input
                    type="number"
                    value={cmsContent.referral.minWithdrawalAmount || 1000}
                    onChange={(e) => setCmsContent({
                      ...cmsContent,
                      referral: { ...cmsContent.referral, minWithdrawalAmount: parseInt(e.target.value) }
                    })}
                    min="100"
                    step="100"
                  />
                  <p className="text-sm text-slate-500">
                    Users can request withdrawal when their earnings reach this amount
                  </p>
                </div>
                <Button onClick={handleSaveReferralSettings} className="gap-2">
                  <Settings className="w-4 h-4" />
                  Save Referral Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMS Gateway Tab */}
          <TabsContent value="sms" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>SMS Gateway Settings</CardTitle>
                <CardDescription>Configure SMS providers for OTP verification</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Gateway Selection */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-900">Active Gateway</h3>
                  <p className="text-sm text-slate-600">Select which SMS gateway to use for sending OTPs</p>
                  
                  {/* Demo Gateway */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="gateway"
                        value="demo"
                        checked={smsSettings.active_gateway === 'demo'}
                        onChange={(e) => setSmsSettings({...smsSettings, active_gateway: 'demo'})}
                        className="w-4 h-4"
                      />
                      <div>
                        <p className="font-medium">Demo Mode</p>
                        <p className="text-sm text-slate-600">For testing - OTP logged to console</p>
                      </div>
                    </div>
                    <Switch
                      checked={smsSettings.demo_enabled}
                      onCheckedChange={(checked) => setSmsSettings({...smsSettings, demo_enabled: checked})}
                    />
                  </div>

                  {/* MSG91 Gateway */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="radio"
                        name="gateway"
                        value="msg91"
                        checked={smsSettings.active_gateway === 'msg91'}
                        onChange={(e) => setSmsSettings({...smsSettings, active_gateway: 'msg91'})}
                        className="w-4 h-4"
                        disabled={!smsSettings.msg91_enabled}
                      />
                      <div className="flex-1">
                        <p className="font-medium">MSG91</p>
                        <p className="text-sm text-slate-600">Popular Indian SMS provider</p>
                        {smsSettings.msg91_enabled && (
                          <div className="mt-3 space-y-2">
                            <Input
                              placeholder="MSG91 Auth Key"
                              value={smsSettings.msg91_auth_key}
                              onChange={(e) => setSmsSettings({...smsSettings, msg91_auth_key: e.target.value})}
                              type="password"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                placeholder="Sender ID (default: MSGAPI)"
                                value={smsSettings.msg91_sender_id}
                                onChange={(e) => setSmsSettings({...smsSettings, msg91_sender_id: e.target.value})}
                              />
                              <Input
                                placeholder="Route (default: default)"
                                value={smsSettings.msg91_route}
                                onChange={(e) => setSmsSettings({...smsSettings, msg91_route: e.target.value})}
                              />
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTestSMS('msg91')}
                            >
                              Test MSG91
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <Switch
                      checked={smsSettings.msg91_enabled}
                      onCheckedChange={(checked) => setSmsSettings({...smsSettings, msg91_enabled: checked})}
                    />
                  </div>

                  {/* Fast2SMS Gateway */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="radio"
                        name="gateway"
                        value="fast2sms"
                        checked={smsSettings.active_gateway === 'fast2sms'}
                        onChange={(e) => setSmsSettings({...smsSettings, active_gateway: 'fast2sms'})}
                        className="w-4 h-4"
                        disabled={!smsSettings.fast2sms_enabled}
                      />
                      <div className="flex-1">
                        <p className="font-medium">Fast2SMS</p>
                        <p className="text-sm text-slate-600">Budget-friendly Indian SMS service</p>
                        {smsSettings.fast2sms_enabled && (
                          <div className="mt-3 space-y-2">
                            <Input
                              placeholder="Fast2SMS API Key"
                              value={smsSettings.fast2sms_api_key}
                              onChange={(e) => setSmsSettings({...smsSettings, fast2sms_api_key: e.target.value})}
                              type="password"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                placeholder="Sender ID (default: FSTSMS)"
                                value={smsSettings.fast2sms_sender_id}
                                onChange={(e) => setSmsSettings({...smsSettings, fast2sms_sender_id: e.target.value})}
                              />
                              <Input
                                placeholder="Route (default: otp)"
                                value={smsSettings.fast2sms_route}
                                onChange={(e) => setSmsSettings({...smsSettings, fast2sms_route: e.target.value})}
                              />
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTestSMS('fast2sms')}
                            >
                              Test Fast2SMS
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <Switch
                      checked={smsSettings.fast2sms_enabled}
                      onCheckedChange={(checked) => setSmsSettings({...smsSettings, fast2sms_enabled: checked})}
                    />
                  </div>
                </div>

                {/* OTP Configuration */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-lg font-semibold text-slate-900">OTP Configuration</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>OTP Length</Label>
                      <Input
                        type="number"
                        min="4"
                        max="8"
                        value={smsSettings.otp_length}
                        onChange={(e) => setSmsSettings({...smsSettings, otp_length: parseInt(e.target.value)})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Expiry (minutes)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="15"
                        value={smsSettings.otp_expiry_minutes}
                        onChange={(e) => setSmsSettings({...smsSettings, otp_expiry_minutes: parseInt(e.target.value)})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Attempts</Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={smsSettings.otp_max_attempts}
                        onChange={(e) => setSmsSettings({...smsSettings, otp_max_attempts: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleSaveSMSSettings} className="gap-2">
                    <Save className="w-4 h-4" />
                    Save SMS Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


        </Tabs>
      </div>

      {/* Edit College Modal */}
      <Dialog open={editCollegeModal} onOpenChange={setEditCollegeModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit College</DialogTitle>
            <DialogDescription>Update college information</DialogDescription>
          </DialogHeader>
          {editingCollege && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>College Name</Label>
                  <Input
                    value={editingCollege.name}
                    onChange={(e) => setEditingCollege({...editingCollege, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <select
                    value={editingCollege.type}
                    onChange={(e) => setEditingCollege({...editingCollege, type: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-300 rounded-md"
                  >
                    <option value="IIT">IIT</option>
                    <option value="NIT">NIT</option>
                    <option value="IIIT">IIIT</option>
                    <option value="GFTI">GFTI</option>
                    <option value="Private">Private</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={editingCollege.state}
                    onChange={(e) => setEditingCollege({...editingCollege, state: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={editingCollege.city}
                    onChange={(e) => setEditingCollege({...editingCollege, city: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditCollegeModal(false)}>Cancel</Button>
                <Button onClick={handleSaveCollege}>Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Branch Modal */}
      <Dialog open={editBranchModal} onOpenChange={setEditBranchModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Branch & Cutoffs</DialogTitle>
            <DialogDescription>Update branch details and cutoff ranks</DialogDescription>
          </DialogHeader>
          {editingBranch && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Branch Name</Label>
                  <Input
                    value={editingBranch.branch.name}
                    onChange={(e) => setEditingBranch({
                      ...editingBranch,
                      branch: {...editingBranch.branch, name: e.target.value}
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Branch Code</Label>
                  <Input
                    value={editingBranch.branch.code}
                    onChange={(e) => setEditingBranch({
                      ...editingBranch,
                      branch: {...editingBranch.branch, code: e.target.value}
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Annual Fees (₹)</Label>
                  <Input
                    type="number"
                    value={editingBranch.branch.fees}
                    onChange={(e) => setEditingBranch({
                      ...editingBranch,
                      branch: {...editingBranch.branch, fees: parseInt(e.target.value)}
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total Seats</Label>
                  <Input
                    type="number"
                    value={editingBranch.branch.seats}
                    onChange={(e) => setEditingBranch({
                      ...editingBranch,
                      branch: {...editingBranch.branch, seats: parseInt(e.target.value)}
                    })}
                  />
                </div>
              </div>

              {/* JEE Main Cutoffs - 5 Years */}
              {editingBranch.branch.cutoffs.jeeMain && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-blue-600">JEE Main Cutoffs (5-Year Trends)</h4>
                  {[2024, 2023, 2022, 2021, 2020].map(year => (
                    <div key={year} className="mb-4 last:mb-0">
                      <p className="text-sm font-medium text-slate-700 mb-2">Year {year}</p>
                      <div className="grid md:grid-cols-4 gap-3">
                        {Object.keys(editingBranch.branch.cutoffs.jeeMain[year] || {}).map(category => {
                          const cutoffData = editingBranch.branch.cutoffs.jeeMain[year]?.[category];
                          const cutoffValue = typeof cutoffData === 'object' ? cutoffData?.closing : cutoffData;
                          return (
                            <div key={category} className="space-y-1">
                              <Label className="text-xs uppercase">{category}</Label>
                              <Input
                                type="number"
                                value={cutoffValue || 0}
                                onChange={(e) => {
                                  const updated = {...editingBranch};
                                  if (!updated.branch.cutoffs.jeeMain[year]) {
                                    updated.branch.cutoffs.jeeMain[year] = {};
                                  }
                                  // Store as object with opening/closing
                                  const currentData = updated.branch.cutoffs.jeeMain[year][category];
                                  if (typeof currentData === 'object') {
                                    updated.branch.cutoffs.jeeMain[year][category] = {
                                      ...currentData,
                                      closing: parseInt(e.target.value)
                                    };
                                  } else {
                                    updated.branch.cutoffs.jeeMain[year][category] = parseInt(e.target.value);
                                  }
                                  setEditingBranch(updated);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* JEE Advanced Cutoffs - 5 Years */}
              {editingBranch.branch.cutoffs.jeeAdvanced && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-green-600">JEE Advanced Cutoffs (5-Year Trends)</h4>
                  {[2024, 2023, 2022, 2021, 2020].map(year => (
                    <div key={year} className="mb-4 last:mb-0">
                      <p className="text-sm font-medium text-slate-700 mb-2">Year {year}</p>
                      <div className="grid md:grid-cols-4 gap-3">
                        {Object.keys(editingBranch.branch.cutoffs.jeeAdvanced[year] || {}).map(category => {
                          const cutoffData = editingBranch.branch.cutoffs.jeeAdvanced[year]?.[category];
                          const cutoffValue = typeof cutoffData === 'object' ? cutoffData?.closing : cutoffData;
                          return (
                            <div key={category} className="space-y-1">
                              <Label className="text-xs uppercase">{category}</Label>
                              <Input
                                type="number"
                                value={cutoffValue || 0}
                                onChange={(e) => {
                                  const updated = {...editingBranch};
                                  if (!updated.branch.cutoffs.jeeAdvanced[year]) {
                                    updated.branch.cutoffs.jeeAdvanced[year] = {};
                                  }
                                  // Store as object with opening/closing
                                  const currentData = updated.branch.cutoffs.jeeAdvanced[year][category];
                                  if (typeof currentData === 'object') {
                                    updated.branch.cutoffs.jeeAdvanced[year][category] = {
                                      ...currentData,
                                      closing: parseInt(e.target.value)
                                    };
                                  } else {
                                    updated.branch.cutoffs.jeeAdvanced[year][category] = parseInt(e.target.value);
                                  }
                                  setEditingBranch(updated);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditBranchModal(false)}>Cancel</Button>
                <Button onClick={handleSaveBranch}>Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Role Edit Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole?.id ? 'Edit Role' : 'Create New Role'}</DialogTitle>
            <DialogDescription>
              Configure permissions for this user role
            </DialogDescription>
          </DialogHeader>
          {editingRole && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Role Name</Label>
                  <Input
                    placeholder="e.g., manager"
                    value={editingRole.name}
                    onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                    disabled={editingRole.id}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Display Name</Label>
                  <Input
                    placeholder="e.g., Manager"
                    value={editingRole.display_name}
                    onChange={(e) => setEditingRole({ ...editingRole, display_name: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label className="mb-3 block">Permissions</Label>
                <div className="grid md:grid-cols-2 gap-3">
                  {Object.keys(editingRole.permissions || {}).map((permission) => (
                    <div key={permission} className="flex items-center space-x-2 p-2 bg-slate-50 rounded">
                      <input
                        type="checkbox"
                        id={`perm-${permission}`}
                        checked={editingRole.permissions[permission]}
                        onChange={(e) => setEditingRole({
                          ...editingRole,
                          permissions: {
                            ...editingRole.permissions,
                            [permission]: e.target.checked
                          }
                        })}
                        className="w-4 h-4"
                      />
                      <Label htmlFor={`perm-${permission}`} className="text-sm cursor-pointer">
                        {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowRoleDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!editingRole.name || !editingRole.display_name) {
                      toast({
                        title: "Error",
                        description: "Please fill all required fields",
                        variant: "destructive"
                      });
                      return;
                    }
                    try {
                      if (editingRole.id) {
                        // Update existing role
                        await axios.put(`${BACKEND_URL}/api/admin/roles/${editingRole.id}`, {
                          name: editingRole.name,
                          display_name: editingRole.display_name,
                          permissions: editingRole.permissions
                        });
                        toast({ title: "Role updated successfully" });
                      } else {
                        // Create new role
                        await axios.post(`${BACKEND_URL}/api/admin/roles`, {
                          name: editingRole.name,
                          display_name: editingRole.display_name,
                          permissions: editingRole.permissions
                        });
                        toast({ title: "Role created successfully" });
                      }
                      setShowRoleDialog(false);
                      // Reload roles
                      const response = await axios.get(`${BACKEND_URL}/api/admin/roles`);
                      setUserRoles(response.data.roles || []);
                    } catch (error) {
                      toast({
                        title: "Error",
                        description: error.response?.data?.detail || "Failed to save role",
                        variant: "destructive"
                      });
                    }
                  }}
                >
                  {editingRole.id ? 'Update Role' : 'Create Role'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;